import React, { useEffect, useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import useAuth from '../../providers/Auth/Auth.provider';
import TwMainView from '../../components/TwMainView/TwMainView';
import TwTab from '../../components/TwTab/TwTab';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import { TabPanel } from '../../components/TwTabPanel/TwTabPanel';
import AddIcon from '@mui/icons-material/Add';
import Divisions from './Divisions/Divisions';
import Clients from './Customers/Clients';
import Carriers from './Carriers/Carriers';
import { USER_ROLE } from '../../providers/User/User.model';
import { AppSettingsTabs } from '../Settings/AppSettings/AppSettings';
import TwCompaniesModal from './CompaniesModal/TwCompaniesModal';
import CreateClientContact from './CompaniesModal/CreateClientContact';
import { ClientResponse } from '../../redux/api/customer/customer.model';
import OwnerOperators from './OwnerOperators/OwnerOperators';
import { useNavigate, useParams } from 'react-router-dom';

export enum CompaniesEntities {
  customer = 'customer',
  carrier = 'carrier',
  division = 'division',
  ownerOperator = 'ownerOperator'
}

const Companies: React.FC = (): JSX.Element => {
  usePageTitle('Companies | Truckbase');
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const { page } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [showContactModal, setShowContactModal] = useState<boolean>(false);
  const [isFirstContact, setIsFirstContact] = useState<boolean>(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  const isOwnerOperatorsEnabled = getTenantSetting('owner-operators');
  const isCarriersEnabled = getTenantSetting('multiple-carriers');

  useEffect(() => {
    if (page) {
      switch (page) {
        case CompaniesEntities.customer:
          setTab(0);
          break;
        case CompaniesEntities.carrier:
          isCarriersEnabled && setTab(1);
          break;
        case CompaniesEntities.ownerOperator:
          isOwnerOperatorsEnabled && setTab(2);
          break;
        default:
          setTab(0);
      }
    }
  }, [page, isCarriersEnabled, isOwnerOperatorsEnabled]);

  /** Page header actions */
  const getPageActions = (): JSX.Element[] => {
    return [
      <TwButton
        label="New company"
        startIcon={<AddIcon />}
        type={TwButtonType.primary}
        key="1"
        action={(): void => setOpenModal(true)}
      />
    ];
  };

  const handleTabChange = (e: React.SyntheticEvent, newTab: number): void => {
    let path = '/companies';
    switch (newTab) {
      case 0:
        path = `/companies/${CompaniesEntities.customer}`;
        break;
      case 1:
        path = `/companies/${CompaniesEntities.carrier}`;
        break;
      case 2:
        path = `/companies/${CompaniesEntities.ownerOperator}`;
        break;
    }
    navigate(path);
    setTab(newTab);
  };

  const closeModal = (): void => {
    setOpenModal(false);
  };

  const closeCreateContactModal = (): void => {
    setShowContactModal(false);
    setIsFirstContact(false);
    setSelectedCustomerId('');
  };
  const onClientCreateSuccess = (newClient: ClientResponse): void => {
    setSelectedCustomerId(newClient.id);
    setIsFirstContact(true);
    setShowContactModal(true);
  };
  const getSuccessAction = (response: any): (() => void) | undefined => {
    if (response.company_type === 'CUSTOMER') {
      onClientCreateSuccess(response);
      return;
    }

    return undefined;
  };

  return (
    <TwMainView actionButtons={getPageActions()} title="Companies">
      <AppSettingsTabs
        variant="standard"
        value={tab}
        onChange={handleTabChange}
        aria-label="TwTabs"
        role="TwTabs"
      >
        <TwTab key={0} value={0} label={'Customers'} role="tab" disableRipple />
        {isCarriersEnabled && (
          <TwTab key={1} value={1} label={'Carriers'} role="tab" disableRipple />
        )}
        {isOwnerOperatorsEnabled && (
          <TwTab key={2} value={2} label={'Owner Operators'} role="tab" disableRipple />
        )}
      </AppSettingsTabs>

      <TabPanel value={tab} index={0}>
        <Clients />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <Carriers />
      </TabPanel>
      {isOwnerOperatorsEnabled && (
        <TabPanel value={tab} index={2}>
          <OwnerOperators />
        </TabPanel>
      )}

      <TwCompaniesModal open={openModal} onClose={closeModal} onSuccess={getSuccessAction} />
      <CreateClientContact
        open={showContactModal}
        onClose={closeCreateContactModal}
        isFirst={isFirstContact}
        customerId={selectedCustomerId}
      />
    </TwMainView>
  );
};

export default Companies;
